import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Banknote, Plus, Upload, FileDown, RefreshCw, AlertTriangle, Search, Settings2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { rcApi, errMsg, fmt, thisMonth } from "./rcApi";
import { RiderCashAddDialog } from "./RiderCashAddDialog";
import { RiderCashLedgerDialog } from "./RiderCashLedgerDialog";
import { RiderCashDeposits } from "./RiderCashDeposits";
import { RiderCashMonthEnd } from "./RiderCashMonthEnd";
import { personMatch } from "../../lib/searchText";

const Stat = ({ label, value, tone, testid }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`mt-1 text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const Toolbar = ({ onAdd, onImported, limit, onLimit }) => {
  const ref = useRef();
  const [lim, setLim] = useState("");
  useEffect(() => setLim(String(limit ?? "")), [limit]);
  const run = async (p, label) => {
    try { const r = await p; toast.success(`${label}: ${r.added} جديد، ${r.updated} محدّث`); if (r.errors?.length || r.unmatched?.length) toast.warning(`تعذّر مطابقة ${(r.errors || r.unmatched).length} صف`); onImported(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button data-testid="rc-add-btn" onClick={onAdd}><Plus className="h-4 w-4" />حركة يدوية</Button>
      <Button variant="outline" data-testid="rc-import-ninja-btn" onClick={() => run(rcApi.importNinja(thisMonth()), "استيراد COD نينجا")}><RefreshCw className="h-4 w-4" />استيراد COD من نينجا</Button>
      <Button variant="outline" data-testid="rc-upload-btn" onClick={() => ref.current?.click()}><Upload className="h-4 w-4" />رفع Excel</Button>
      <input ref={ref} type="file" accept=".xlsx" hidden data-testid="rc-upload-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(rcApi.upload(f), "رفع الملف"); }} />
      <a href={rcApi.templateUrl} data-testid="rc-template-link" className="flex items-center gap-1 text-xs text-blue-600"><FileDown className="h-4 w-4" />نموذج Excel</a>
      <div className="mr-auto flex items-center gap-2 rounded-lg border bg-white px-2 py-1">
        <Settings2 className="h-4 w-4 text-slate-400" /><span className="text-xs text-slate-500">حد النقد المسموح</span>
        <Input data-testid="rc-limit-input" type="number" className="h-8 w-24" value={lim} onChange={(e) => setLim(e.target.value)} />
        <Button size="sm" variant="ghost" data-testid="rc-limit-save" onClick={() => onLimit(Number(lim))}>حفظ</Button>
      </div>
    </div>
  );
};

const BalancesTable = ({ rows, onOpen }) => (
  <div className="overflow-x-auto rounded-xl border bg-white">
    <table className="w-full text-right text-sm">
      <thead className="bg-slate-50 text-xs text-slate-500"><tr>{["المندوب", "المحصّل", "المورّد", "بانتظار التأكيد", "مخصوم من الراتب", "الرصيد المستحق", "آخر حركة"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.employee_id} onClick={() => onOpen(r)} className={`cursor-pointer border-t transition-colors hover:bg-blue-50/50 ${r.over_limit ? "bg-rose-50/60" : ""}`} data-testid={`rc-row-${r.employee_id}`}>
            <td className="p-3 font-medium">{r.over_limit && <AlertTriangle className="ml-1 inline h-4 w-4 text-rose-500" />}{r.name}<span className="mr-1 text-xs text-slate-400">{r.employee_number}</span></td>
            <td className="p-3 tabular-nums">{fmt(r.collected)}</td>
            <td className="p-3 tabular-nums text-emerald-600">{fmt(r.deposited)}</td>
            <td className="p-3 tabular-nums text-sky-600">{fmt(r.pending)}</td>
            <td className="p-3 tabular-nums text-amber-600">{fmt(r.deducted)}</td>
            <td className={`p-3 font-bold tabular-nums ${r.balance > 0 ? "text-rose-600" : "text-slate-500"}`} data-testid={`rc-row-balance-${r.employee_id}`}>{fmt(r.balance)}</td>
            <td className="p-3 text-xs text-slate-500">{r.last_date}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <p className="p-10 text-center text-sm text-slate-400">لا توجد حركات نقد بعد</p>}
  </div>
);

export default function RiderCashPanel({ employees = [] }) {
  const [data, setData] = useState({ rows: [], totals: {}, limit: 0, pending_count: 0, over_count: 0 });
  const [q, setQ] = useState("");
  const [add, setAdd] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => rcApi.summary().then(setData).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const saveLimit = async (v) => {
    if (!(v >= 0)) return toast.error("قيمة غير صالحة");
    try { await rcApi.settingsSave(v); toast.success("تم حفظ الحد"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const T = data.totals || {};
  const rows = data.rows.filter((r) => personMatch(r, q));
  return (
    <div className="space-y-5" dir="rtl" data-testid="rider-cash-panel">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-emerald-100 p-2.5"><Banknote className="h-6 w-6 text-emerald-700" /></div>
        <div><h2 className="font-heading text-xl font-bold">إدارة النقد للمناديب</h2><p className="text-sm text-slate-500">متابعة الكاش المحصّل (COD) والتوريد والخصم من الراتب مع قيود محاسبية تلقائية</p></div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="إجمالي المحصّل" value={fmt(T.collected)} tone="text-slate-800" testid="rc-stat-collected" />
        <Stat label="إجمالي المورّد" value={fmt(T.deposited)} tone="text-emerald-600" testid="rc-stat-deposited" />
        <Stat label="بانتظار التأكيد" value={fmt(T.pending)} tone="text-sky-600" testid="rc-stat-pending" />
        <Stat label="الرصيد غير المورّد" value={fmt(T.balance)} tone="text-rose-600" testid="rc-stat-balance" />
        <Stat label={`تجاوزوا الحد (${fmt(data.limit)})`} value={data.over_count} tone="text-rose-600" testid="rc-stat-over" />
      </div>
      <Toolbar onAdd={() => setAdd(true)} onImported={load} limit={data.limit} onLimit={saveLimit} />
      <Tabs defaultValue="balances" dir="rtl">
        <TabsList>
          <TabsTrigger value="balances" data-testid="rc-tab-balances">الأرصدة</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="rc-tab-deposits">إيصالات التوريد {data.pending_count > 0 && <span className="mr-1 rounded-full bg-rose-500 px-1.5 text-[10px] text-white">{data.pending_count}</span>}</TabsTrigger>
          <TabsTrigger value="month" data-testid="rc-tab-month">إقفال الشهر</TabsTrigger>
        </TabsList>
        <TabsContent value="balances" className="space-y-3">
          <div className="relative max-w-xs"><Search className="absolute right-2 top-2.5 h-4 w-4 text-slate-400" /><Input data-testid="rc-search" className="pr-8" placeholder="بحث بالاسم أو الرقم" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <BalancesTable rows={rows} onOpen={setOpen} />
        </TabsContent>
        <TabsContent value="deposits"><RiderCashDeposits onChanged={load} /></TabsContent>
        <TabsContent value="month"><RiderCashMonthEnd onChanged={load} /></TabsContent>
      </Tabs>
      {add && <RiderCashAddDialog employees={employees} onClose={() => setAdd(false)} onSaved={load} />}
      {open && <RiderCashLedgerDialog row={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
